export type GamificationMetricKey = "activity" | "pipeline" | "wins" | "revenueBooked";
export type GamificationPeriodKey = "daily" | "weekly" | "monthly";
export type GamificationTheme = "equalizer" | "test_tubes" | "marquee";

export type SnapshotMetric = {
  count: number;
  goal: number;
  pct: number;
  paceForecast: number;
  paceStatus: "ahead" | "on_track" | "behind";
  flagged?: number;
};

export type SnapshotPeriod = Record<GamificationMetricKey, SnapshotMetric> & {
  start: string;
  end: string;
  elapsedPct: number;
};

/**
 * Year-to-date totals for a rep — a totals-only view used by the Goals tile
 * "Year" toggle. No goal target and no pacing status: just a running count per
 * metric from the start of the current year through now.
 */
export type YearToDateTotals = {
  start: string;
  end: string;
  activity: number;
  pipeline: number;
  wins: number;
};

// Coach Mode v2 (Task #311) — a quantified aspiration's live progress, piggy-
// backed on the gamification snapshot so the global celebration bus can detect
// a bar crossing 100% and fire *bonus* coach confetti (stacking on top of any
// booking/win celebration in the same tick). Only aspirations that actually
// have a target (a progress bar) are included.
export type CoachAspirationProgress = {
  id: string;
  title: string;
  domain: "business" | "personal" | string;
  target: number;
  current: number;
  unit: string | null;
  /** 0..1, clamped. */
  pct: number;
  completed: boolean;
};

export type GamificationSnapshot = {
  enabled: boolean;
  theme: GamificationTheme;
  reduceMotion: boolean;
  silentMode: boolean;
  soundsAllowed: boolean;
  date: string;
  daily: SnapshotPeriod;
  weekly: SnapshotPeriod;
  monthly: SnapshotPeriod;
  qualityFlags: { calls: number; emails: number };
  /**
   * Active quantified coach aspirations for this user (may be empty / omitted
   * when coach mode is off). Present for celebration detection only — the
   * Calendar Hub coach panel fetches its own richer list.
   */
  coachAspirations?: CoachAspirationProgress[];
};

export const METRIC_LABELS: Record<GamificationMetricKey, string> = {
  activity: "Outreach",
  pipeline: "New Leads",
  wins: "Bookings",
  revenueBooked: "Revenue Booked",
};

export const METRIC_SUBTITLES: Record<GamificationMetricKey, string> = {
  activity: "Calls + emails",
  pipeline: "Leads created",
  wins: "Bookings closed",
  revenueBooked: "Revenue confirmed",
};

export const METRIC_COLORS: Record<GamificationMetricKey, string> = {
  activity: "#3b82f6",
  pipeline: "#a855f7",
  wins: "#22c55e",
  revenueBooked: "#f59e0b",
};

/** Metrics that display as dollar amounts rather than counts. */
export const METRIC_IS_CURRENCY: Partial<Record<GamificationMetricKey, true>> = {
  revenueBooked: true,
};

/** Format a metric value for display. */
export function formatMetricValue(key: GamificationMetricKey, value: number): string {
  if (!METRIC_IS_CURRENCY[key]) return String(Math.round(value));
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)}`;
}
